import algoliasearch from 'algoliasearch/lite';
import { autocomplete, getAlgoliaResults } from '@algolia/autocomplete-js';
import '@algolia/autocomplete-theme-classic';

const ALGOLIA_APP_ID = process.env.ALGOLIA_APP_ID;
const ALGOLIA_SEARCH_API_KEY = process.env.ALGOLIA_SEARCH_API_KEY;
const ENV_TYPE = process.env.ENV_TYPE;
const debug = true;

const searchClient = algoliasearch(
  ALGOLIA_APP_ID,
  ALGOLIA_SEARCH_API_KEY
);

// Default result template: highlighted name linking to the hit url.
const nameTemplate = ({ item, components, html }) =>
  html`<a href="${item.url}">${components.Highlight({ hit: item, attribute: 'name', tagName: 'strong' })}</a>`;

// Build an Algolia autocomplete source. indexName is derived as `${ENV_TYPE}_${id}`.
// `params` merges into the query params (defaults to hitsPerPage: 32); `itemTemplate`
// overrides the default name link (used by the item-number source).
function makeSource({ id, title, params = {}, itemTemplate = nameTemplate }) {
  return {
    sourceId: id,
    getItems({ query }) {
      return getAlgoliaResults({
        searchClient,
        queries: [
          {
            indexName: ENV_TYPE + '_' + id,
            query,
            params: {
              hitsPerPage: 32,
              ...params,
            },
          },
        ],
      });
    },
    getItemUrl({ item }) {
      return item.url;
    },
    templates: {
      header({ items, html }) {
        if (items.length === 0) {
          return null;
        }
        return html`<span class="aa-SourceHeaderTitle">${title}</span><span class="aa-SourceHeaderLine"></span>`;
      },
      item: itemTemplate,
    },
  };
}

// Mount an autocomplete widget if its container is present in the DOM.
function mountAutocomplete({ id, placeholder, minLength, submitUrl, sources, defaultActiveItemId }) {
  if (!document.getElementById(id)) {
    return;
  }

  const config = {
    container: '#' + id,
    insights: true,
    placeholder,
    debug,
    shouldPanelOpen: ({ state }) => state.query.length >= minLength,
    renderNoResults({ state, render, html }, root) {
      render(html`<div class="aa-noResults">No suggestions for "${state.query}".</div>`, root);
    },
    getSources() {
      return sources.map(makeSource);
    },
  };

  if (defaultActiveItemId !== undefined) {
    config.defaultActiveItemId = defaultActiveItemId;
  }

  if (submitUrl) {
    config.onSubmit = ({ state, setQuery }) => {
      setQuery(state.query);
      window.location.href = submitUrl + state.query;
    };
  }

  autocomplete(config);
}

// Shared source list for the site-wide keyword search widgets.
const SITE_SOURCES = [
  { id: 'ProductCategory', title: 'Product categories' },
  { id: 'Product', title: 'Products' },
  { id: 'ServiceCategory', title: 'Service categories' },
  { id: 'Service', title: 'Services' },
  { id: 'AppCategory', title: 'App categories' },
  { id: 'App', title: 'Apps' },
  { id: 'Accessory', title: 'Accessories' },
  { id: 'Item', title: 'Items' },
  { id: 'ResourceType', title: 'Resource types' },
  { id: 'Resource', title: 'Documentation & downloads' },
  { id: 'Page', title: 'Other pages' },
];

function buildAutoCompletes() {
  // Item search matches on the item number and shows "#<number> - <name>".
  const itemNumberTemplate = ({ item, components, html }) =>
    html`<a href="${item.url}">#${components.Highlight({ hit: item, attribute: 'number', tagName: 'strong' })} - ${item.name}</a>`;

  mountAutocomplete({
    id: 'item-autocomplete',
    placeholder: 'Search by item number',
    minLength: 2,
    defaultActiveItemId: 0,
    sources: [
      { id: 'Item', title: 'Items', params: { restrictSearchableAttributes: ['number'] }, itemTemplate: itemNumberTemplate },
    ],
  });

  mountAutocomplete({
    id: 'support-autocomplete',
    placeholder: 'Search support',
    minLength: 3,
    submitUrl: '/support/product-support/search?term=',
    sources: [
      { id: 'Resource', title: 'Product support', params: { filters: 'purpose:Support' } },
    ],
  });

  mountAutocomplete({
    id: 'keyword-autocomplete',
    placeholder: 'Search by keyword',
    minLength: 3,
    submitUrl: '/search/?keyword=',
    sources: SITE_SOURCES,
  });

  mountAutocomplete({
    id: 'searchpage-autocomplete',
    placeholder: 'Search by keyword',
    minLength: 3,
    submitUrl: '/search/?keyword=',
    sources: SITE_SOURCES,
  });
}

window.addEventListener('DOMContentLoaded', function () {
  buildAutoCompletes();
  // window.aa = Algolia search-insights global. Not bundled: the autocomplete
  // insights plugin (`insights: true` above) loads it from the CDN if missing,
  // so it exists once buildAutoCompletes() has run.
  window.aa('init', {
    appId: ALGOLIA_APP_ID,
    apiKey: ALGOLIA_SEARCH_API_KEY,
    useCookie: true
  });
});
